import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import type { SVGProps } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClientButton } from '@/components/client-ui/button';
import { ClientWizardLayout } from '@/components/client-ui/ClientWizardLayout';
import { Alert } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useClientCombinedSearch } from '@/features/booking/hooks/useClientSearch';
import { parseWizardSearch, requireMode } from '@/features/booking/state/wizardSearch';
import { sessionsService } from '@/features/sessions/services/sessionsService';
import { ApiError } from '@/lib/errors';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute('/client/book/confirm')({
  component: ClientBookConfirmStep,
  validateSearch: (s: Record<string, unknown>) => parseWizardSearch(s),
  beforeLoad: ({ search }) => {
    try {
      requireMode(search);
    } catch {
      if (search.practitionerId === undefined) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: '/client/book/practitioner' });
      }
      if (
        search.date === undefined ||
        search.startTime === undefined ||
        search.durationMinutes === undefined
      ) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: '/client/book/when', search });
      }
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/client/book/details', search });
    }
  },
});

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatDateLong(iso: string): string {
  const [yStr, mStr, dStr] = iso.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  const date = new Date(y, m - 1, d);
  const dow = WEEKDAYS[date.getDay()] ?? '';
  const month = MONTHS[m - 1] ?? '';
  return `${dow}, ${String(d)} ${month} ${String(y)}`;
}

function format12(hm: string): string {
  const [hStr, mStr] = hm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0
    ? `${String(displayHour)} ${suffix}`
    : `${String(displayHour)}:${String(m).padStart(2, '0')} ${suffix}`;
}

function addMinutes(hm: string, min: number): string {
  const [hStr, mStr] = hm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  const total = h * 60 + m + min;
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

// Volunteers carry a single `displayName` (e.g. "Sand") with null first/last
// name; psychologists supply first/last. Prefer the explicit displayName when
// present so volunteer hero/avatar don't degrade to a "Practitioner" / "P"
// placeholder. Mirrors BookPractitionerSummaryCard's summaryName.
function fullName(p: {
  firstName: string | null;
  lastName: string | null;
  displayName?: string | null;
}): string {
  if (p.displayName !== undefined && p.displayName !== null && p.displayName.length > 0)
    return p.displayName;
  const joined = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
  return joined.length > 0 ? joined : 'Practitioner';
}

function avatarInitial(p: {
  firstName: string | null;
  lastName: string | null;
  displayName?: string | null;
}): string {
  return fullName(p).trim().charAt(0).toUpperCase() || '?';
}

function kindLabel(kind: 'volunteer' | 'psychologist' | undefined): string {
  return kind === 'volunteer' ? 'Listener' : 'Psychologist';
}

// Twin-sparkle glyph mirroring /public/new-session.svg. Stroke uses currentColor
// so the surrounding text-color drives the tint — keeps the chip cohesive when
// rebound for the follow-up variant.
function NewSessionIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path
        d="M4.66602 9.33268C7.24335 9.33268 9.33268 7.24335 9.33268 4.66602C9.33268 7.24335 11.422 9.33268 13.9993 9.33268C11.422 9.33268 9.33268 11.422 9.33268 13.9993C9.33268 11.422 7.24335 9.33268 4.66602 9.33268Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 4.33333C3.28865 4.33333 4.33333 3.28865 4.33333 2C4.33333 3.28865 5.37801 4.33333 6.66667 4.33333C5.37801 4.33333 4.33333 5.37801 4.33333 6.66667C4.33333 5.37801 3.28865 4.33333 2 4.33333Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClientBookConfirmStep() {
  const { t } = useTranslation('clientBooking');
  const navigate = useNavigate();
  const search = Route.useSearch();
  const user = useAuthStore((s) => s.user);

  const consentId = useId();
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  const date = search.date ?? '';
  const startTime = search.startTime ?? '';
  const duration = search.durationMinutes ?? 60;
  const sessionMode = search.sessionMode ?? 'online';
  const sessionType = search.sessionType ?? 'new';

  // Pull the real practitioner from the same availability search the When step
  // uses so the hero shows avatar + name + role label instead of a raw ID.
  const searchQuery = useClientCombinedSearch({ date, mode: undefined });
  const own = (searchQuery.data?.results ?? []).find(
    (r) => r.practitioner.id === search.practitionerId,
  );
  const practitioner = own?.practitioner;

  async function onSubmit() {
    if (!consent || user === null) return;
    if (
      search.practitionerId === undefined ||
      search.date === undefined ||
      search.startTime === undefined ||
      search.durationMinutes === undefined ||
      search.sessionMode === undefined
    ) {
      return;
    }
    setSubmitting(true);
    setTopError(null);
    try {
      await sessionsService.create({
        clientId: user.id,
        practitionerId: search.practitionerId,
        date: search.date,
        startTime: search.startTime,
        durationMinutes: search.durationMinutes,
        sessionType: search.sessionType ?? 'new',
        sessionMode: search.sessionMode,
        consentGiven: true,
        note: search.note,
      });
      void navigate({ to: '/client/sessions', replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          // Rate-limit messages from the BE are often opaque ("Too many
          // requests"); the canned copy reads better.
          setTopError(t('wizard.step.confirm.errors.rateLimited'));
        } else if (err.message.length > 0) {
          // Trust the BE message whenever it is supplied — it covers
          // cases the FE cannot pre-classify (e.g. on 409 the BE may
          // return "Practitioner is not available for this time /
          // location" for an availability conflict, OR "Slot already
          // booked" for a race). A canned slot-taken copy would mask
          // the more informative reasons.
          setTopError(err.message);
        } else if (err.status === 409 || err.status === 400) {
          // BE supplied no message on a conflict-ish status — most common
          // cause is a stale slot, so fall back to the slot-taken copy.
          setTopError(t('wizard.step.confirm.errors.slotTaken'));
        } else {
          setTopError(t('wizard.step.confirm.errors.generic'));
        }
      } else {
        setTopError(t('wizard.step.confirm.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const onBack = () => {
    void navigate({ to: '/client/book/details', search });
  };

  const headingName = practitioner !== undefined ? fullName(practitioner) : 'Practitioner';
  const role = kindLabel(practitioner?.practitionerKind);
  const typeLabel = sessionType === 'follow_up' ? 'Follow-up' : 'New Session';
  const modeLabel = sessionMode === 'in_person' ? 'In-person' : 'Online';

  return (
    <ClientWizardLayout current={4} contentMaxWidth="narrow">
      <div className="mx-auto flex w-full max-w-175 flex-col items-stretch gap-2.5">
        <h1 className="font-client-display mb-3 text-center text-[18px] leading-7 font-semibold text-(--color-client-primary-500) sm:mb-4 sm:text-[20px] sm:leading-8">
          {t('wizard.step.confirm.title')}
        </h1>

        {topError !== null ? <Alert variant="error">{topError}</Alert> : null}

        {/* Hero card per Figma 2946:1318 — gap-5, asymmetric pt-10 pb-5 px-5
            padding. Avatar pill: 124x124 mint-100 rounded-full housing a
            100x100 primary-tint avatar circle so the mint ring stays visible
            behind the initial.
            PANEL, not glass: the card contains an opaque white "Online · 45
            Mins" chip (:284) and an opaque #f6f6f6 Date & Time well (:297),
            both of which would out-solid a 62%-translucent parent. Measured
            flat at 375 the top edge — where the eye lands first — was a
            +4/+2/+2 step with no border and no shadow at all. r24 is pinned by
            the frame, so the two utilities are composed by hand instead of
            using GlassCard's `panel` variant, which is bolted to
            rounded-client-panel (r30); both radius classes survive cn(). */}
        <div className="bg-client-panel rounded-client-xl flex w-full flex-col items-center gap-4 px-4 pt-8 pb-4 sm:gap-5 sm:px-5 sm:pt-10 sm:pb-5">
          {/* Until the practitioner resolves this card used to state, in the
              same weight as the real thing, that the session was with
              "Practitioner · Psychologist" behind a "?" avatar. On the screen
              where the user commits to a booking, an invented identity is
              strictly worse than an obvious placeholder — so while the query
              is in flight we show the shape of the answer and none of its
              content. Same box sizes as the loaded state, so confirming does
              not move under the user. */}
          {searchQuery.isLoading ? (
            <div
              data-testid="confirm-identity-loading"
              className="flex flex-col items-center gap-3 sm:gap-3.5"
            >
              <Skeleton className="h-20 w-20 rounded-full sm:h-26 sm:w-26" />
              <div className="flex flex-col items-center gap-1.5">
                <Skeleton className="h-5 w-24 rounded-full sm:h-6" />
                <Skeleton className="h-6 w-56 rounded-full sm:h-8" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 sm:gap-3.5">
              <div className="bg-client-primary-tint-avatar flex h-20 w-20 items-center justify-center overflow-hidden rounded-full shadow-[0_0_0_1px_rgba(3,68,92,0.06),0_8px_24px_-8px_rgba(15,95,140,0.18)] sm:h-26 sm:w-26">
                {practitioner?.profilePicture !== undefined &&
                practitioner.profilePicture !== null ? (
                  <img
                    src={practitioner.profilePicture}
                    alt={headingName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-[30px] leading-none font-semibold text-[var(--color-client-primary-500)] sm:text-[40px]">
                    {practitioner !== undefined ? avatarInitial(practitioner) : '?'}
                  </span>
                )}
              </div>
              <div className="flex flex-col items-center gap-0.5">
                <p className="text-[13px] leading-5 font-medium text-[var(--color-client-muted-foreground-soft)] sm:text-[16px] sm:leading-6">
                  Session With
                </p>
                <p className="text-center text-[16px] leading-6 text-[var(--color-client-foreground)] sm:text-[20px] sm:leading-8">
                  <span className="font-medium">{headingName}</span>
                  <span className="font-normal"> · </span>
                  <span className="font-medium">{role}</span>
                </p>
              </div>
            </div>
          )}

          <hr className="border-client-line-100 w-full border-t" />

          <div className="flex flex-wrap justify-center gap-3">
            <span className="border-client-primary-tint-mid bg-client-primary-tint-soft inline-flex items-center gap-2 rounded-[10px] border py-1 pr-3 pl-2.5">
              <NewSessionIcon className="h-4 w-4 text-(--color-client-primary-500)" />
              <span className="text-[14px] leading-6 font-medium text-(--color-client-primary-500)">
                {typeLabel}
              </span>
            </span>
            <span className="border-client-line-100 bg-client-surface inline-flex items-center gap-1 rounded-[10px] border px-3.5 py-1 text-[var(--color-client-foreground)]">
              <span className="text-[14px] leading-6 font-medium">{modeLabel}</span>
              <span className="text-[14px] leading-6 font-medium">·</span>
              <span className="font-client-display text-[12px] leading-6 font-medium tracking-[var(--tracking-client-mono-eyebrow)] uppercase">
                {String(duration)} Mins
              </span>
            </span>
          </div>

          {/* Date & Time card — label stacks above value on mobile so the
              long date+time string ("Wednesday, 13 May 2026 · 10 AM - 10:45
              AM") has the full card width to lay out instead of fighting a
              side-by-side label for ~180px of room. */}
          <div className="border-client-line-100 bg-client-line-50 flex w-full flex-col items-center justify-center gap-1 rounded-[14px] border p-3 sm:flex-row sm:gap-3">
            <span className="text-[13px] leading-5 font-medium text-[var(--color-client-primary-500)] sm:text-[16px] sm:leading-6">
              Date &amp; Time
            </span>
            <span className="text-center text-[14px] leading-5 font-medium text-[var(--color-client-foreground)] sm:text-[16px] sm:leading-8">
              {formatDateLong(date)} · {format12(startTime)} -{' '}
              {format12(addMinutes(startTime, duration))}
            </span>
          </div>
        </div>

        {/* Consent card per Figma 2946:1339 — 21px padding, gap-4, items-center.
            The old `border-client-line-100 border` is deleted along with the
            flat fill. That border measured +1/+1/+3 against the wash at 375 —
            a 1px line the card was paying for and not getting; the utility's
            135deg ring replaces it, and keeping both would double-draw the edge.
            PANEL, not glass, and that is a measured call rather than a
            tiering one. This card sits low on the confirm page, where the page
            wash has gone warm (#f4efea -> #fdefe1), and a 62% white glass fill
            lets that warmth through so the fill stops contributing any step at
            all. A/B at 375, edge-vs-wash max per channel L/T/R:
              flat 17/28/23   glass 16/26/21   panel 23/38/28
            Panel is the only variant that beats the flat fill on all three
            edges. It does not fight the hero panel above it for attention —
            the hero is 339px tall with an avatar, this is a checkbox row of
            body copy, so content carries the hierarchy, not the fill.
            Composed as raw utilities for the same reason as the hero above —
            GlassCard's `panel` variant carries rounded-client-panel (r30) and
            this card is pinned to r24, and both radius classes would survive
            cn(). (It could not use the primitive anyway: GlassCardProps
            extends React.HTMLAttributes<HTMLElement>, which has no `htmlFor`.) */}
        <label
          htmlFor={consentId}
          className="bg-client-panel rounded-client-xl flex w-full cursor-pointer items-start gap-3 p-4 sm:gap-4 sm:p-5.25"
        >
          <Checkbox
            id={consentId}
            checked={consent}
            onCheckedChange={(v) => {
              setConsent(v === true);
            }}
            // The focus pair is a call-site override of the shared staff
            // primitive, matching the consent box on /client/call-now.
            // ui/checkbox.tsx ships `focus-visible:ring-2 ring-primary-200`,
            // which in the portal resolves to the staff green #80b0a4 and
            // paints flush against the box with no gap. The primitive has 17
            // consumers and only two of them are client surfaces (this one
            // and ConfirmAndStartStep), so it stays untouched and the two
            // client call sites opt out locally. `ring-0` and `shadow-*`
            // write different slots of the composed box-shadow, so they
            // compose instead of fighting.
            className="mt-0.5 h-[18px] w-[18px] shrink-0 rounded-[4px] border-[var(--color-client-line-400)] focus-visible:shadow-(--shadow-client-focus-ring) focus-visible:ring-0 data-[state=checked]:border-[var(--color-client-primary-500)] data-[state=checked]:bg-[var(--color-client-primary-500)]"
          />
          {/* Two paragraphs, two keys. JSX collapses whitespace, so the break
              between the consent body and the confirmation line has to be
              structural rather than a newline inside one string. */}
          <span className="flex flex-col gap-2 text-[13px] leading-5 font-medium text-[var(--color-client-muted-foreground-mid)] sm:text-[14px]">
            <span>{t('wizard.step.confirm.consent')}</span>
            <span>{t('wizard.step.confirm.consentConfirm')}</span>
          </span>
        </label>

        <div className="mt-2 flex items-center justify-center gap-2 sm:gap-2.5">
          <ClientButton
            variant="secondary"
            onClick={onBack}
            disabled={submitting}
            className="h-10 flex-1 rounded-[12px] sm:w-35 sm:flex-initial"
          >
            {t('wizard.step.confirm.back')}
          </ClientButton>
          <ClientButton
            variant="primary"
            disabled={!consent || submitting}
            loading={submitting}
            onClick={() => {
              void onSubmit();
            }}
            className="h-10 flex-1 rounded-[12px] px-4 sm:flex-initial sm:px-6"
          >
            {t('wizard.step.confirm.submit')}
          </ClientButton>
        </div>
      </div>
    </ClientWizardLayout>
  );
}
